import { Skeleton } from '@vizit/atoms';
import { ScoreColorVariant, ScoreDescriptions } from '@vizit/constants';
import { cva } from '@vizit/theme';
import { getScoreColorVariant, getScoreDescription } from '@vizit/utils';
import type * as React from 'react';
import { Text, TextSize } from '../text';

interface ScorePillProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The ref to use for the score pill.
   *
   * @optional
   * @type React.Ref<HTMLDivElement>
   * @default undefined
   */
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Score value to calculate display text and color.
   *
   * @optional
   * @type number | null
   * @default null
   */
  score: number | null;
  /**
   * Whether to omit the score and display UNSCORED.
   *
   * @optional
   * @type boolean
   * @default false
   */
  omitScore?: boolean;
  /**
   * Whether is loading.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
}

const pillClasses = cva({
  base: 'inline-flex items-center justify-center px-4 py-1 min-w-24 font-medium',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'bg-score-not-applicable text-score-not-applicable-strong',
      [ScoreColorVariant.VeryLow]: 'bg-score-very-low text-score-very-low-strong',
      [ScoreColorVariant.Low]: 'bg-score-low text-score-low-strong',
      [ScoreColorVariant.Moderate]: 'bg-score-moderate text-score-moderate-strong',
      [ScoreColorVariant.High]: 'bg-score-high text-score-high-strong',
      [ScoreColorVariant.VeryHigh]: 'bg-score-very-high text-score-very-high-strong',
    },
  },
  defaultVariants: {
    colorVariant: ScoreColorVariant.NotApplicable,
  },
});

/**
 * A badge to display a status or label.
 *
 * @param {ScorePillProps} props - The props to use for the pill.
 */
function ScorePill(props: ScorePillProps) {
  const { ref, children, className, score, omitScore = false, isLoading = false, ...other } = props;

  const formattedScore = score === null ? ScoreDescriptions.NotApplicable : getScoreDescription(score);
  const colorVariant = omitScore ? ScoreColorVariant.NotApplicable : getScoreColorVariant(score);

  return (
    <>
      {isLoading && <Skeleton className="px-4 py-1 h-12 w-24" variant="square" />}
      {!isLoading && (
        <div ref={ref} className={pillClasses({ className, colorVariant })} {...other}>
          <Text elementType="span" size={TextSize.Xs} showEllipsis={false} className="text-inherit">
            {omitScore ? ScoreDescriptions.Unscored : formattedScore}
          </Text>
        </div>
      )}
    </>
  );
}

export type { ScorePillProps };
export { ScorePill };
